import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import type { AnswerWidgets } from '@openx/components-core/lib/AiChat/answerWidgets';
import { type Message, MessageRole, type OpenXAgentMessage } from '@openx/components-core/lib/AiChat/types';
import { Fragment, type ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { ChatMessage } from '../ChatMessage/ChatMessage';
import type { RichContentActionEvent } from '../RichComponents/utils/richContent';
import { ChatContentLoader } from './components/ChatContentLoader';
import { ErrorMessage } from './components/ErrorMessage';
import { Footer } from './components/Footer';
import { useRichContentConfig } from './hooks/useRichContentConfig';
import { messagesContainerSx, rootSx } from './styles';
import { collectOpenXAgentAnswerWidgets } from './utils/collectOpenXAgentAnswerWidgets';
import { isContainerNearBottom } from './utils/isContainerNearBottom';
import { scrollContainerToBottom } from './utils/scrollContainerToBottom';

export type AiChatProps = {
  errorMessage?: string | null;
  isThinking: boolean;
  isLoading: boolean;
  messages: OpenXAgentMessage[];
  sendMessage: (query: string) => void;
  thinkingProcessMessages?: string[];
  onRichContentAction?: (event: RichContentActionEvent) => void;
  feedbackActions?: (message: Message, widgets: AnswerWidgets | undefined) => ReactNode;
  callsToAction?: (message: Message) => ReactNode;
  customFooter?: ReactNode;
};

export const AiChat = ({
  errorMessage,
  isThinking,
  isLoading,
  messages,
  sendMessage,
  thinkingProcessMessages = [],
  onRichContentAction,
  feedbackActions,
  callsToAction,
  customFooter,
}: AiChatProps) => {
  const inputRef = useRef<{ setValue: (value: string) => void }>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const shouldFollowRef = useRef(true);
  const previousMessagesLengthRef = useRef(0);
  const lastForcedAgentTimestampRef = useRef<string | null>(null);
  const hasMessages = messages.length > 0 || errorMessage;
  const widgetsByInvocation = useMemo(
    () =>
      collectOpenXAgentAnswerWidgets(
        messages.filter((message): message is Message => message.role !== MessageRole.THINKING)
      ),
    [messages]
  );
  const richContentConfig = useRichContentConfig(messages, onRichContentAction);

  const handleContainerScroll = useCallback(() => {
    const container = messagesContainerRef.current;

    if (container) shouldFollowRef.current = isContainerNearBottom(container);
  }, []);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) {
      return;
    }

    const previousLength = previousMessagesLengthRef.current;
    const appendedMessages = previousLength < messages.length ? messages.slice(previousLength) : [];
    const isDisplayingThinkingMessages = thinkingProcessMessages.length > 0 && isThinking;

    let newFinalAgentTimestamp: string | null = null;
    let appendedUserMessage = false;
    let appendedEvent = false;

    for (const message of appendedMessages) {
      if (message.role === MessageRole.EVENT || message.role === MessageRole.WIDGET) {
        appendedEvent = true;
      } else if (message.role === MessageRole.USER) {
        appendedUserMessage = true;
      } else if (message.timestamp !== lastForcedAgentTimestampRef.current) {
        newFinalAgentTimestamp = message.timestamp;
      }
    }

    if (newFinalAgentTimestamp !== null) {
      shouldFollowRef.current = true;
      scrollContainerToBottom(container, 'smooth');
      lastForcedAgentTimestampRef.current = newFinalAgentTimestamp;
    } else if (isDisplayingThinkingMessages) {
      shouldFollowRef.current = true;
      scrollContainerToBottom(container, 'smooth');
    } else if (appendedUserMessage) {
      shouldFollowRef.current = true;
      scrollContainerToBottom(container, 'smooth');
    } else if (appendedEvent && shouldFollowRef.current) {
      scrollContainerToBottom(container, 'auto');
    }

    previousMessagesLengthRef.current = messages.length;
  }, [messages, thinkingProcessMessages, isThinking]);

  return (
    <Stack direction="column" sx={rootSx}>
      <Box
        ref={messagesContainerRef}
        onScroll={handleContainerScroll}
        data-test="messages-container"
        sx={messagesContainerSx(hasMessages)}
      >
        {isLoading && <ChatContentLoader />}

        {messages
          .filter(message => message.role !== MessageRole.WIDGET)
          .map(message => {
            const showFeedbackActions = message.role === 'agent' && feedbackActions !== undefined;
            const showCTAs = message.role === 'agent' && callsToAction !== undefined;
            const widgets =
              message.role === MessageRole.AGENT ? widgetsByInvocation.get(message.invocation_id) : undefined;

            return (
              <Fragment key={message.timestamp}>
                <ChatMessage message={message} widgets={widgets} richContent={richContentConfig} />
                {showFeedbackActions && feedbackActions(message, widgets)}
                {showCTAs && callsToAction(message)}
              </Fragment>
            );
          })}

        {isThinking && (
          <ChatMessage
            message={{ role: MessageRole.THINKING, messages: thinkingProcessMessages, timestamp: String(Date.now()) }}
          />
        )}

        {errorMessage && <ErrorMessage errorMessage={errorMessage} />}
      </Box>

      {customFooter || <Footer isLoading={isThinking || isLoading} sendMessage={sendMessage} inputRef={inputRef} />}
    </Stack>
  );
};
